import React from "/vendor/react";
import gql from "/vendor/graphql-tag";

import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "/vendor/@material-ui/core";

import { useSearchParams, useFilterParams } from "/lib/component/util";
import { ListController } from "/lib/component/controller";
import { Loader, TableListEmptyState } from "/lib/component/base";

import CheckConfigDialog from "/lib/component/partial/CheckConfigDialog";
import ClearSilencedEntriesDialog from "/lib/component/partial/ClearSilencedEntriesDialog";
import Pagination from "/lib/component/partial/Pagination";
import SilenceEntryDialog from "/lib/component/partial/SilenceEntryDialog";

import {
  usePublishCheckStatusToast,
  useExecuteCheckStatusToast,
  useManageResourceStatusToast,
} from "/lib/component/toast";

import ChecksListHeader from "./ChecksListHeader";
import ChecksListItem from "./ChecksListItem";

export interface ChecksListVariables {
  limit: number;
  offset: number;
  order: string;
  filters: string[];
}

export const checksListFragments = {
  namespace: gql`
    fragment ChecksList_namespace on Namespace {
      id
      checks(
        limit: $limit
        offset: $offset
        orderBy: $order
        filters: $filters
      ) @connection(key: "checks", filter: ["filters", "orderBy"]) {
        nodes {
          id
          deleted @client
          name
          namespace
          subscriptions
          silences {
            name
            ...ClearSilencedEntriesDialog_silence
          }

          ...ChecksListItem_check
        }

        pageInfo {
          ...Pagination_pageInfo
        }
      }

      ...ChecksListHeader_namespace
    }

    ${ChecksListHeader.fragments.namespace}
    ${ChecksListItem.fragments.check}
    ${ClearSilencedEntriesDialog.fragments.silence}
    ${Pagination.fragments.pageInfo}
  `,
};

interface Check {
  id: string;
  deleted: boolean;
  name: string;
  namespace: string;
  subscriptions: string[];
  silences: unknown[];
  pageInfo: unknown;
}

interface PageInfo {
  totalCount: number;
}

interface Namespace {
  checks: {
    pageInfo: PageInfo;
    nodes: Check[];
  };
}

interface Props {
  editable: boolean;
  loading: boolean;
  limit?: number;
  namespace?: Namespace;
  namespaceName?: string;
  offset?: number;
  order?: string;
  refetch(): void;
  onCreateSilence: (_: any) => void;
  onDeleteSilence: (_: any) => void;
  onExecute: (_: any) => Promise<any>;
  onPublish: (_: any) => Promise<any>;
  onDeleteCheck: (_: any) => Promise<any>;
}

const ChecksList = ({
  editable,
  loading,
  limit,
  namespace,
  namespaceName,
  offset,
  order,
  refetch,
  onCreateSilence,
  onDeleteSilence,
  onExecute,
  onPublish,
  onDeleteCheck,
}: Props) => {
  const [, setParams] = useSearchParams();
  const [filters, setFilters] = useFilterParams();

  const [silence, setSilence] = React.useState<any>(null);
  const [unsilence, setUnsilence] = React.useState<any>(null);
  // null = closed, { checkName: null } = new check, { checkName: "x" } = edit
  const [configDialog, setConfigDialog] = React.useState<any>(null);

  const createPublishCheckStatusToast = usePublishCheckStatusToast();
  const createExecuteCheckStatusToast = useExecuteCheckStatusToast();
  const createManageResourceStatusToast = useManageResourceStatusToast();

  const resolvedNamespace =
    namespaceName || (namespace && (namespace as any).name) || "";

  const deleteChecks = (checks: Check[]) => {
    createManageResourceStatusToast(
      Promise.all(checks.map((check) => onDeleteCheck({ id: check.id }))),
      {
        resourceType: "check",
        action: "delete",
        batch: checks.length > 1,
      },
    );
  };

  const setChecksPublish = (checks: Check[], publish: boolean = true) => {
    checks.forEach((check) => {
      const promise = onPublish({
        id: check.id,
        publish,
      });

      createPublishCheckStatusToast(promise, {
        checkName: check.name,
        publish,
      });
    });
  };

  const silenceChecks = (checks: Check[]) => {
    const targets = checks
      .filter((check) => check.silences.length === 0)
      .map((check) => ({
        namespace: check.namespace,
        check: check.name,
      }));

    if (targets.length === 1) {
      setSilence({ props: {}, ...targets[0] });
    } else if (targets.length) {
      setSilence({ props: {}, targets });
    }
  };

  const clearSilences = (checks: Check[]) => {
    setUnsilence(
      checks.reduce(
        (memo: unknown[], check) => [...memo, ...check.silences],
        [],
      ),
    );
  };

  const executeChecks = (checks: Check[]) => {
    checks.forEach(({ id, name, subscriptions, namespace: checkNamespace }) => {
      const promise = onExecute({ id, subscriptions });

      createExecuteCheckStatusToast(promise, {
        checkName: name,
        namespace: checkNamespace,
        entityName: undefined,
      });
    });
  };

  const items: Check[] = namespace
    ? namespace.checks.nodes.filter((ch: Check) => !ch.deleted)
    : [];

  return (
    <ListController
      items={items}
      getItemKey={(check: Check) => check.id}
      renderEmptyState={() => {
        return (
          <TableRow>
            <TableCell>
              <TableListEmptyState
                loading={loading}
                primary="No results matched your query."
                secondary="
              Try refining your search query in the search box. The filter
              buttons above are also a helpful way of quickly finding entities.
            "
              />
            </TableCell>
          </TableRow>
        );
      }}
      renderItem={({
        key,
        item: check,
        hovered,
        setHovered,
        selectedCount,
        selected,
        setSelected,
      }) => (
        <ChecksListItem
          key={key}
          editable={editable}
          editing={selectedCount > 0}
          check={check}
          hovered={hovered}
          onHover={editable ? setHovered : () => null}
          selected={selected}
          onChangeSelected={setSelected}
          onClickClearSilences={() => clearSilences([check])}
          onClickExecute={() => executeChecks([check])}
          onClickSetPublish={(publish: boolean) =>
            setChecksPublish([check], publish)
          }
          onClickSilence={() => silenceChecks([check])}
          onClickEdit={() => setConfigDialog({ checkName: check.name })}
          onClickDelete={() => deleteChecks([check])}
        />
      )}
    >
      {({ children, selectedItems, setSelectedItems, toggleSelectedItems }) => (
        <Paper>
          <Loader loading={loading}>
            <ChecksListHeader
              editable={editable}
              filters={filters}
              namespace={namespace}
              onChangeFilters={setFilters}
              onClickClearSilences={() => clearSilences(selectedItems)}
              onClickExecute={() => {
                executeChecks(selectedItems);
                setSelectedItems([]);
              }}
              onClickSetPublish={(publish: boolean) => {
                setChecksPublish(selectedItems, publish);
                setSelectedItems([]);
              }}
              onClickSilence={() => silenceChecks(selectedItems)}
              onClickNew={() => setConfigDialog({ checkName: null })}
              onClickDelete={() => {
                deleteChecks(selectedItems);
                setSelectedItems([]);
              }}
              order={order}
              rowCount={items.length}
              selectedItems={selectedItems}
              toggleSelectedItems={toggleSelectedItems}
            />

            <Table>
              <TableBody>{children}</TableBody>
            </Table>

            <Pagination
              limit={limit}
              offset={offset}
              pageInfo={namespace && namespace.checks.pageInfo}
              onChangeQuery={(update: any) =>
                setParams((params) => ({ ...params, ...update }))
              }
            />

            {silence && (
              <SilenceEntryDialog
                values={silence}
                onSave={onCreateSilence}
                onClose={() => {
                  setSilence(null);
                  setSelectedItems([]);
                  refetch();
                }}
              />
            )}

            <ClearSilencedEntriesDialog
              silences={unsilence}
              open={!!unsilence}
              onSave={onDeleteSilence}
              onClose={() => {
                setUnsilence(null);
                setSelectedItems([]);
                refetch();
              }}
            />

            {configDialog && (
              <CheckConfigDialog
                namespace={resolvedNamespace}
                checkName={configDialog.checkName}
                onClose={() => setConfigDialog(null)}
                onSuccess={() => {
                  setSelectedItems([]);
                  refetch();
                }}
              />
            )}
          </Loader>
        </Paper>
      )}
    </ListController>
  );
};

ChecksList.defaultProps = {
  editable: true,
  namespace: null,
  namespaceName: undefined,
  loading: false,
  limit: undefined,
  offset: undefined,
  filters: {},
  onChangeFilters: () => null,
  onDeleteCheck: () => Promise.resolve(),
};

export default ChecksList;
